import React from 'react';
import formSchema from '../../data/formSchema.json';

const GuidePrintout = React.forwardRef(({ results = null }, ref) => {
  const { overallScore, stage, dimensionScores = {}, recommendations = [], formData = {} } = results || {};

  // Helper to format answer values for display
  const formatAnswer = (field, value, sectionId) => {
    if (value === undefined || value === null || value === '') return 'Not answered';
    
    // Handle arrays (checkboxes)
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'None selected';
    }
    
    // Handle radio fields (stored as indices)
    if (field.type === 'radio' && field.options) {
      const index = typeof value === 'string' ? parseInt(value) : value;
      if (!isNaN(index) && index >= 0 && index < field.options.length) {
        return field.options[index];
      }
    }
    
    // Handle select fields (stored as text)
    if (field.type === 'select') {
      return value;
    }
    
    // Handle scale fields
    if (field.type === 'scale') {
      return `${value} / 5`;
    }
    
    // Default: return as-is
    return value;
  };

  return (
    <div id="assessment-report" ref={ref} className="bg-white text-gray-900 max-w-[700px] mx-auto p-8 print:p-0">
      {/* Header with Logo */}
      <div className="text-center mb-8 pb-6 border-b-2 border-gray-300">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Strategy & Readiness Assessment</h1>
        <p className="text-sm text-gray-600">Framework & Results Report</p>
        <p className="text-xs text-gray-500 mt-2">omnigeist.com</p>
      </div>

      {/* Results Section (if provided) */}
      {results && (
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-300">Your Assessment Results</h2>
          
          {/* Overall Maturity */}
          <div className="mb-8 p-6 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200 page-break-avoid">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Your AI Maturity Level</h3>
              <div className="text-3xl font-bold text-teal-600">{overallScore?.toFixed(1) || 'N/A'}</div>
            </div>
            {stage && (
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">{stage.name}</h4>
                <p className="text-sm text-gray-700">{stage.description}</p>
              </div>
            )}
          </div>

          {/* Dimension Scores */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Dimension Scores</h3>
            <div className="space-y-3">
              {Object.entries(dimensionScores).map(([dimension, score]) => (
                <div key={dimension} className="flex items-center">
                  <div className="w-32 text-sm font-medium text-gray-700 capitalize">{dimension}</div>
                  <div className="flex-1 bg-gray-200 rounded-full h-6 mr-4">
                    <div 
                      className="bg-gradient-to-r from-teal-500 to-blue-500 h-6 rounded-full flex items-center justify-end pr-2"
                      style={{ width: `${(score / 5) * 100}%` }}
                    >
                      <span className="text-xs font-bold text-white">{score?.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="mb-8 page-break">
              <h3 className="text-lg font-bold text-gray-900 mb-4 mt-8">Personalized Recommendations</h3>
              <div className="space-y-4">
                {recommendations.map((rec, idx) => (
                  <div key={idx} className="border-l-4 border-teal-500 pl-4 py-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-white bg-teal-600 px-2 py-1 rounded">{rec.priority}</span>
                      <span className="font-semibold text-gray-800 capitalize">{rec.dimension}</span>
                    </div>
                    <p className="text-sm text-gray-700 font-medium mb-1">{rec.recommendation}</p>
                    <p className="text-xs text-gray-600 italic">{rec.impact}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Your Responses Section */}
          {formData && Object.keys(formData).length > 0 && (
            <div className="mb-8 page-break">
              <h3 className="text-lg font-bold text-gray-900 mb-4 mt-8">Your Assessment Responses</h3>
              <p className="text-xs text-gray-600 mb-6">Complete record of your answers for reference and sharing with stakeholders.</p>
              
              {formSchema.sections.map((section) => {
                const sectionData = formData[section.id] || {};
                const hasAnswers = Object.keys(sectionData).length > 0;
                
                if (!hasAnswers) return null;
                
                return (
                  <div key={section.id} className="mb-6 page-break-avoid">
                    <h4 className="text-md font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                      {section.title}
                    </h4>
                    <div className="space-y-3">
                      {section.fields.map((field) => {
                        const value = sectionData[field.id];
                        
                        // Skip if not answered
                        if (value === undefined || value === null || value === '') return null;
                        if (Array.isArray(value) && value.length === 0) return null;
                        
                        // Skip "other" fields if empty
                        if (field.id.includes('_other') && !value) return null;
                        
                        return (
                          <div key={field.id} className="text-sm">
                            <p className="font-medium text-gray-700 mb-1">{field.label}</p>
                            <p className="text-gray-600 ml-4">
                              {formatAnswer(field, value, section.id)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="page-break my-12 border-t-2 border-dashed border-gray-300"></div>
        </div>
      )}

      {/* Framework Guide */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-300">Assessment Framework Guide</h2>
        
        {/* Overview */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Overview</h3>
          <p className="text-sm text-gray-700 mb-3">
            This framework helps organizations evaluate their readiness for AI integration and transformation. It provides a structured approach for understanding where you currently stand and what steps are needed to move forward.
          </p>
          <p className="text-sm text-gray-700">
            Use this assessment independently to gauge your maturity level and identify areas for investment and development.
          </p>
        </div>

        {/* Purpose */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Purpose of the Assessment</h3>
          <p className="text-sm text-gray-700 mb-3">The AI Strategy and Readiness Assessment helps organizations:</p>
          <ul className="space-y-2 text-sm text-gray-700 ml-4">
            <li>• Understand their current level of AI maturity</li>
            <li>• Identify strengths, gaps, and opportunities in data, technology, people, and governance</li>
            <li>• Prioritize actions aligned with business strategy and capability</li>
            <li>• Build a roadmap for responsible and scalable AI integration</li>
          </ul>
        </div>

        {/* Maturity Model */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">The Maturity Model</h3>
          <p className="text-sm text-gray-700 mb-6">
            The assessment is based on a progressive model of AI capability — from simple access to full-scale operational transformation.
          </p>

          {/* Stage 1 */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg page-break-avoid">
            <h4 className="text-base font-bold text-gray-900 mb-2">Stage 1 – AI Access (AI + Resources)</h4>
            <p className="text-sm text-gray-700 mb-3">Foundational access to AI systems, models, and data. The organization is experimenting and learning.</p>
            <div className="grid grid-cols-2 gap-4 text-xs text-gray-700">
              <div>
                <p className="font-semibold mb-2">Indicators:</p>
                <ul className="space-y-1 ml-2">
                  <li>• Public/enterprise AI tools in use</li>
                  <li>• Limited governance or strategy</li>
                  <li>• RAG system exploration</li>
                  <li>• Early AI pilots on org data</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Key Questions:</p>
                <ul className="space-y-1 ml-2">
                  <li>• Who uses AI and for what?</li>
                  <li>• What data could enhance AI?</li>
                  <li>• Is there a security policy?</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Stage 2 */}
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg page-break-avoid">
            <h4 className="text-base font-bold text-gray-900 mb-2">Stage 2 – AI Integration (AI + Tools)</h4>
            <p className="text-sm text-gray-700 mb-3">AI capabilities become embedded within systems and workflows to enhance productivity and collaboration.</p>
            <div className="grid grid-cols-2 gap-4 text-xs text-gray-700">
              <div>
                <p className="font-semibold mb-2">Indicators:</p>
                <ul className="space-y-1 ml-2">
                  <li>• AI copilots in productivity tools</li>
                  <li>• AI features in business systems</li>
                  <li>• Simple single-platform automation</li>
                  <li>• Defined AI oversight roles</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Key Questions:</p>
                <ul className="space-y-1 ml-2">
                  <li>• Which systems have AI features?</li>
                  <li>• Are employees trained?</li>
                  <li>• Do you have AI governance?</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Stage 3 */}
          <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg page-break-avoid">
            <h4 className="text-base font-bold text-gray-900 mb-2">Stage 3 – AI Orchestration (AI + Workflows)</h4>
            <p className="text-sm text-gray-700 mb-3">The organization coordinates AI and automation through orchestration systems, with AI handling multi-step processes.</p>
            <div className="grid grid-cols-2 gap-4 text-xs text-gray-700">
              <div>
                <p className="font-semibold mb-2">Indicators:</p>
                <ul className="space-y-1 ml-2">
                  <li>• Workflow orchestrators deployed</li>
                  <li>• Specialized multi-step agents</li>
                  <li>• Cross-system coordination</li>
                  <li>• Clear human oversight</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Key Questions:</p>
                <ul className="space-y-1 ml-2">
                  <li>• Are workflows AI-triggered?</li>
                  <li>• How is responsibility shared?</li>
                  <li>• Is monitoring in place?</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Stage 4 */}
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg page-break-avoid">
            <h4 className="text-base font-bold text-gray-900 mb-2">Stage 4 – AI Transformation (AI + Mission)</h4>
            <p className="text-sm text-gray-700 mb-3">AI is systematically integrated across core operations. AI provides scaled execution while humans maintain strategic authority.</p>
            <div className="grid grid-cols-2 gap-4 text-xs text-gray-700">
              <div>
                <p className="font-semibold mb-2">Indicators:</p>
                <ul className="space-y-1 ml-2">
                  <li>• Redesigned human-AI processes</li>
                  <li>• Real-time predictive insights</li>
                  <li>• Continuous learning loops</li>
                  <li>• Strong governance frameworks</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Key Questions:</p>
                <ul className="space-y-1 ml-2">
                  <li>• Is AI in strategic planning?</li>
                  <li>• How does leadership use AI?</li>
                  <li>• What ensures responsible AI?</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Scoring Dimensions */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Scoring and Self-Evaluation</h3>
          <p className="text-sm text-gray-700 mb-4">Each stage can be evaluated across the following dimensions:</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-gray-100 rounded">
              <p className="text-sm font-semibold text-gray-900">Strategy</p>
              <p className="text-xs text-gray-600">AI aligned with business objectives</p>
            </div>
            <div className="p-3 bg-gray-100 rounded">
              <p className="text-sm font-semibold text-gray-900">Data</p>
              <p className="text-xs text-gray-600">Sources integrated, secure, AI-ready</p>
            </div>
            <div className="p-3 bg-gray-100 rounded">
              <p className="text-sm font-semibold text-gray-900">Technology</p>
              <p className="text-xs text-gray-600">Tools and models scalable</p>
            </div>
            <div className="p-3 bg-gray-100 rounded">
              <p className="text-sm font-semibold text-gray-900">People</p>
              <p className="text-xs text-gray-600">Teams trained and empowered</p>
            </div>
            <div className="p-3 bg-gray-100 rounded col-span-2">
              <p className="text-sm font-semibold text-gray-900">Governance</p>
              <p className="text-xs text-gray-600">Oversight, ethics, and compliance in place</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 p-3 bg-teal-50 border border-teal-200 rounded">
            <strong>Scoring:</strong> Use a 1–5 scale (1 = Early Exploration, 5 = Fully Operational) to assess maturity across these dimensions.
          </p>
        </div>

        {/* How to Use */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-3">How to Use This Framework</h3>
          <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
            <div className="p-3 bg-gray-100 rounded">
              <p className="font-semibold mb-1">Self-assess</p>
              <p className="text-xs">Review and score your organization across five dimensions</p>
            </div>
            <div className="p-3 bg-gray-100 rounded">
              <p className="font-semibold mb-1">Identify Gaps</p>
              <p className="text-xs">Pinpoint where practices fall short of goals</p>
            </div>
            <div className="p-3 bg-gray-100 rounded">
              <p className="font-semibold mb-1">Plan Actions</p>
              <p className="text-xs">Outline initiatives to advance to next stage</p>
            </div>
            <div className="p-3 bg-gray-100 rounded">
              <p className="font-semibold mb-1">Engage Partners</p>
              <p className="text-xs">Reach out for implementation guidance</p>
            </div>
          </div>
        </div>

        {/* Outcome */}
        <div className="mb-8 p-4 bg-teal-50 border border-teal-200 rounded-lg">
          <h3 className="text-lg font-bold text-gray-900 mb-2">Outcome</h3>
          <p className="text-sm text-gray-700">
            This assessment provides a clear view of where you are today and where you can go next. By using it, organizations can build confidence, clarity, and alignment in their AI journey — turning potential into operational advantage.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500 pt-6 border-t border-gray-300 mt-8">
        <p className="mb-1">AI Strategy & Readiness Assessment Framework</p>
        <p className="font-medium">omnigeist.com</p>
        <p className="mt-2 text-gray-400">This framework is free to use and share with your team.</p>
      </div>
    </div>
  );
});

GuidePrintout.displayName = 'GuidePrintout';

export default GuidePrintout;

